class NoonProductStory extends HTMLElement {
  connectedCallback() {
    if (this.dataset.noonStoryReady === 'true') return;
    this.dataset.noonStoryReady = 'true';

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktopQuery = window.matchMedia('(min-width: 1024px)');
    this.stage = this.querySelector('[data-noon-story-stage]');
    this.frames = Array.from(this.querySelectorAll('[data-noon-story-frame]')).sort(
      (a, b) => Number(a.dataset.noonStoryIndex) - Number(b.dataset.noonStoryIndex)
    );
    this.steps = Array.from(this.querySelectorAll('[data-noon-story-step-index]')).sort(
      (a, b) => Number(a.dataset.noonStoryStepIndex) - Number(b.dataset.noonStoryStepIndex)
    );
    this.jumpButtons = Array.from(this.querySelectorAll('[data-noon-story-jump-index]'));
    this.modeButtons = Array.from(this.querySelectorAll('.noon-story__modes [data-noon-story-jump-index]'));
    this.roomChoiceButtons = Array.from(this.querySelectorAll('.noon-story__room-choice'));
    this.roomChoices = this.querySelector('[data-noon-story-room-choices]');
    this.currentLabel = this.querySelector('[data-noon-story-current-label]');
    this.currentOutput = this.querySelector('[data-noon-story-current]');
    this.totalLabel = this.querySelector('[data-noon-story-total-label]');
    this.range = this.querySelector('[data-noon-story-range]');
    this.total = this.frames.length;
    this.firstRoomIndex = Number(this.dataset.noonStoryFirstRoom || this.total);
    this.activeIndex = 0;

    this.onJumpClick = (event) => {
      event.preventDefault();
      this.navigateToIndex(Number(event.currentTarget.dataset.noonStoryJumpIndex));
    };

    this.onRangeInput = (event) => {
      this.navigateToIndex(Number(event.currentTarget.value) - 1, false);
    };

    this.onRangeChange = (event) => {
      this.navigateToIndex(Number(event.currentTarget.value) - 1, false);
    };

    this.onViewportChange = () => this.setupProgression();

    this.onPointerStart = (event) => {
      if (event.target.closest('[data-noon-story-range], button, a')) return;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
    };

    this.onPointerEnd = (event) => {
      if (this.desktopQuery.matches || this.pointerStartX === undefined) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;
      this.clearPointer();

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
      this.navigateToIndex(this.activeIndex + (deltaX < 0 ? 1 : -1));
    };

    this.onPointerCancel = () => this.clearPointer();

    this.onKeydown = (event) => {
      const previous = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
      const next = event.key === 'ArrowRight' || event.key === 'ArrowDown';
      if (!previous && !next) return;

      event.preventDefault();
      this.navigateToIndex(this.activeIndex + (next ? 1 : -1));
    };

    this.jumpButtons.forEach((button) => button.addEventListener('click', this.onJumpClick));
    this.range?.addEventListener('input', this.onRangeInput);
    this.range?.addEventListener('change', this.onRangeChange);
    this.stage?.addEventListener('pointerdown', this.onPointerStart, { passive: true });
    this.stage?.addEventListener('pointerup', this.onPointerEnd, { passive: true });
    this.stage?.addEventListener('pointercancel', this.onPointerCancel, { passive: true });
    this.stage?.addEventListener('keydown', this.onKeydown);

    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onViewportChange);
    } else {
      this.desktopQuery.addListener(this.onViewportChange);
    }

    this.activateIndex(0);
    if (this.totalLabel) this.totalLabel.textContent = String(this.total).padStart(2, '0');
    if (this.range) this.range.max = String(this.total);
    this.setupProgression();
  }

  disconnectedCallback() {
    this.jumpButtons?.forEach((button) => button.removeEventListener('click', this.onJumpClick));
    this.range?.removeEventListener('input', this.onRangeInput);
    this.range?.removeEventListener('change', this.onRangeChange);
    this.stage?.removeEventListener('pointerdown', this.onPointerStart);
    this.stage?.removeEventListener('pointerup', this.onPointerEnd);
    this.stage?.removeEventListener('pointercancel', this.onPointerCancel);
    this.stage?.removeEventListener('keydown', this.onKeydown);

    if (this.desktopQuery?.removeEventListener) {
      this.desktopQuery.removeEventListener('change', this.onViewportChange);
    } else {
      this.desktopQuery?.removeListener(this.onViewportChange);
    }

    this.stepObserver?.disconnect();
    window.clearTimeout(this.roomTransitionTimer);
  }

  clearPointer() {
    this.pointerStartX = undefined;
    this.pointerStartY = undefined;
  }

  setupProgression() {
    this.stepObserver?.disconnect();
    this.stepObserver = null;

    if (!this.desktopQuery.matches || this.steps.length < 2 || !('IntersectionObserver' in window)) return;

    this.stepObserver = new IntersectionObserver(
      (entries) => {
        if (performance.now() < (this.ignoreObserverUntil || 0)) return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              Math.abs(a.boundingClientRect.top + a.boundingClientRect.height / 2 - window.innerHeight / 2) -
              Math.abs(b.boundingClientRect.top + b.boundingClientRect.height / 2 - window.innerHeight / 2)
          )[0];

        if (visible) this.activateIndex(Number(visible.target.dataset.noonStoryStepIndex));
      },
      { rootMargin: '-43% 0px -43% 0px', threshold: 0 }
    );

    this.steps.forEach((step) => this.stepObserver.observe(step));
  }

  navigateToIndex(requestedIndex, smooth = false) {
    if (!this.total) return;
    const index = Math.max(0, Math.min(this.total - 1, requestedIndex));
    this.ignoreObserverUntil = performance.now() + (this.reduceMotion ? 80 : 1200);
    this.activateIndex(index);

    if (this.desktopQuery.matches) {
      this.steps[index]?.scrollIntoView({
        behavior: this.reduceMotion || !smooth ? 'auto' : 'smooth',
        block: 'center'
      });
    }
  }

  activateIndex(requestedIndex) {
    if (!this.total) return;

    const index = Math.max(0, Math.min(this.total - 1, requestedIndex));
    const previousIndex = this.activeIndex;
    const activeFrame = this.frames[index];
    if (!activeFrame) return;

    const enteringRoom = previousIndex < this.firstRoomIndex && index >= this.firstRoomIndex;
    this.activeIndex = index;
    this.dataset.activeIndex = String(index);
    this.dataset.activeGroup = index >= this.firstRoomIndex ? 'room' : 'artwork';

    this.frames.forEach((frame, frameIndex) => {
      const active = frameIndex === index;
      frame.classList.toggle('is-active', active);
      frame.classList.toggle('is-before', frameIndex < index);
      frame.classList.toggle('is-after', frameIndex > index);
      frame.setAttribute('aria-hidden', String(!active));
    });

    if (enteringRoom && !this.reduceMotion) {
      this.classList.remove('is-entering-room');
      void this.offsetWidth;
      this.classList.add('is-entering-room');
      window.clearTimeout(this.roomTransitionTimer);
      this.roomTransitionTimer = window.setTimeout(() => this.classList.remove('is-entering-room'), 980);
    } else if (index < this.firstRoomIndex) {
      this.classList.remove('is-entering-room');
    }

    const inRoom = index >= this.firstRoomIndex;
    this.modeButtons.forEach((button) => {
      const buttonIndex = Number(button.dataset.noonStoryJumpIndex);
      const active = buttonIndex === 0 ? !inRoom : inRoom;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    this.roomChoiceButtons.forEach((button) => {
      const active = Number(button.dataset.noonStoryJumpIndex) === index;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    if (this.roomChoices) this.roomChoices.hidden = !inRoom;
    if (this.currentLabel) this.currentLabel.textContent = activeFrame.dataset.noonStoryLabel || 'Artwork';

    const progress = this.total > 1 ? (index / (this.total - 1)) * 100 : 100;
    this.style.setProperty('--noon-story-progress', `${progress}%`);

    if (this.currentOutput) this.currentOutput.textContent = String(index + 1).padStart(2, '0');
    if (this.range) {
      this.range.value = String(index + 1);
      this.range.setAttribute('aria-valuetext', `Image ${index + 1} of ${this.total}`);
    }
  }
}

if (!customElements.get('noon-product-story')) {
  customElements.define('noon-product-story', NoonProductStory);
}
